import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'

type Section = 'agency_overview' | 'approach' | 'team' | 'quality_controls' | 'glossary'

const SECTIONS: { key: Section; label: string; placeholder: string }[] = [
  { key: 'agency_overview', label: 'Agency Overview', placeholder: 'Introduce your agency — who you are, your research philosophy, and what makes your approach distinctive.' },
  { key: 'approach', label: 'Research Approach', placeholder: 'Describe how your agency uses Echosome and how it integrates with your proprietary methodology.' },
  { key: 'team', label: 'Research Team', placeholder: 'About the researchers who design and interpret the work. Include credentials relevant to the client.' },
  { key: 'quality_controls', label: 'Quality Controls', placeholder: 'Your QA process — how you review pre-reads, validate results, and sign off before client delivery.' },
  { key: 'glossary', label: 'Glossary', placeholder: 'Agency-specific or client-specific terms. E.g. "Ensemble confidence" means...' },
]

const ECHOSOME_METHODOLOGY_PREVIEW = `## Echosome Methodology

**Ensemble engine:** Seven specialized reasoning passes (Evidence Grounded, Segment Response, Skeptical Researcher, Behavioral Driver, Counterfactual Risk, Quantitative Distribution, Validation Planner) run independently and produce a confidence-weighted prediction.

**Confidence formula:** Wilson interval at 95% on pass agreement, adjusted for evidence coverage and dissent rate.

**Validation gating:** Low-confidence cells (Wilson < 0.60) are automatically flagged for human validation before a result is treated as actionable.

**Ground truth integration:** Results are calibrated against real outcomes via the Calibration Ledger.`

export function MethodologySettingsPage() {
  const [activeTab, setActiveTab] = useState<Section>('agency_overview')
  const [content, setContent] = useState<Record<Section, string>>({
    agency_overview: '',
    approach: '',
    team: '',
    quality_controls: '',
    glossary: '',
  })
  const [showEchosome, setShowEchosome] = useState(true)
  const [preview, setPreview] = useState(false)
  const [saved, setSaved] = useState(false)

  function handleSave() {
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const current = SECTIONS.find(s => s.key === activeTab)!

  return (
    <div className="p-6 space-y-6 max-w-5xl pb-20 md:pb-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Methodology Page</h1>
          <p className="text-sm text-slate-500 mt-1">Customize the methodology appendix that appears at the end of every client report.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setPreview(!preview)}>
            {preview ? 'Edit' : 'Preview'}
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave}>
            {saved ? 'Saved!' : 'Save changes'}
          </Button>
        </div>
      </div>

      {preview ? (
        <Card className="prose prose-sm max-w-none text-deep-ink space-y-6">
          {SECTIONS.map(s => content[s.key] ? (
            <div key={s.key}>
              <h2 className="text-base font-medium text-deep-ink mb-2">{s.label}</h2>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{content[s.key]}</div>
            </div>
          ) : null)}
          {showEchosome && (
            <div className="border-t border-border-gray pt-4">
              <div className="text-sm text-slate-600 whitespace-pre-wrap">{ECHOSOME_METHODOLOGY_PREVIEW}</div>
            </div>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Tab nav */}
          <div className="space-y-1">
            {SECTIONS.map(s => (
              <button
                key={s.key}
                className={cn(
                  'w-full text-left text-sm px-3 py-2 rounded-lg transition-colors',
                  activeTab === s.key
                    ? 'bg-echo-indigo/10 text-echo-indigo font-medium'
                    : 'text-slate-500 hover:bg-soft-panel'
                )}
                onClick={() => setActiveTab(s.key)}
              >
                {s.label}
                {content[s.key] && <span className="ml-1 text-validation-green text-xs">✓</span>}
              </button>
            ))}
            <div className="pt-3 border-t border-border-gray mt-3">
              <label className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg hover:bg-soft-panel">
                <input
                  type="checkbox"
                  checked={showEchosome}
                  onChange={e => setShowEchosome(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-slate-600">Include Echosome methodology</span>
              </label>
            </div>
          </div>

          {/* Editor */}
          <div className="lg:col-span-3">
            <Card>
              <label className="block text-sm font-medium text-deep-ink mb-2">{current.label}</label>
              <textarea
                className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm text-deep-ink focus:outline-none focus:ring-2 focus:ring-human-teal/30 resize-none font-mono"
                rows={18}
                placeholder={current.placeholder}
                value={content[activeTab]}
                onChange={e => setContent(prev => ({ ...prev, [activeTab]: e.target.value }))}
              />
              <p className="text-xs text-slate-400 mt-2">Markdown supported. Headers, bold, lists, code blocks.</p>
            </Card>
          </div>
        </div>
      )}
    </div>
  )
}
